import React, { useMemo } from 'react';
import { MessageSquare, Inbox, AlertCircle } from 'lucide-react';
import { ConversationFilters } from './ConversationFilters';
import { ConversationCard } from './ConversationCard';
import { EmptyState } from '@/components/common/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getApiWorkspacesWorkspaceIdConversations, getGetApiWorkspacesWorkspaceIdConversationsQueryKey } from '@/api/generated/conversations/conversations';
import { useConversationStore } from '@/stores/useConversationStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { MOCK_CONVERSATION_ITEM } from '../mockData';
import type { ConversationItem } from '@/types';
import { cn } from '@/lib/utils';

interface ConversationListProps {
  className?: string;
}

export const ConversationList: React.FC<ConversationListProps> = ({ className }) => {
  const { activeWorkspaceId, workspaces } = useWorkspaceStore();
  const {
    selectedStoreId,
    statusFilter,
    searchKeyword,
    sortOrder,
    customerFilterId,
    resetFilters,
  } = useConversationStore();

  const { hasSeenTour } = useOnboardingStore();

  const isValidWorkspace = workspaces.some((w) => w.id === activeWorkspaceId);

  const {
    data: conversationsData,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!, { customerId: customerFilterId || undefined }),
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getApiWorkspacesWorkspaceIdConversations(activeWorkspaceId!, { 
        skip: pageParam as number, 
        take: 20,
        ...(customerFilterId ? { customerId: customerFilterId } : {})
      });
      return res as unknown as ConversationItem[];
    },
    enabled: !!activeWorkspaceId && isValidWorkspace,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!Array.isArray(lastPage)) return undefined;
      return lastPage.length === 20 ? allPages.length * 20 : undefined;
    },
    staleTime: 10000,
  });

  const rawConversations = useMemo(() => {
    const apiConversations = conversationsData?.pages ? conversationsData.pages.flat() : [];
    if (apiConversations.length === 0 && !hasSeenTour) {
      return [MOCK_CONVERSATION_ITEM];
    }
    return apiConversations;
  }, [conversationsData, hasSeenTour]);

  // Apply filters and sorting
  const filteredConversations = useMemo(() => {
    let result = [...rawConversations];

    // 1. Store filter
    if (selectedStoreId && selectedStoreId !== 'ALL') {
      result = result.filter((c) => c.storeId === selectedStoreId);
    }

    // 2. Status filter
    if (statusFilter !== 'ALL') {
      result = result.filter((c) => {
        const s = String(c.status).toUpperCase();
        switch (statusFilter) {
          case 'UNREAD':
            return (c.unreadCount && c.unreadCount > 0) || s === 'UNREAD';
          case 'WAITING':
            return s === 'WAITINGFORHUMAN' || s === 'WAITING' || s === 'QUEUE' || c.status === 1;
          case 'AI_HANDLING':
            return s === 'ACTIVE' || s === 'AI_HANDLING' || s === 'AI' || c.status === 0;
          case 'HUMAN_ASSIGNED':
            return s === 'HUMANASSIGNED' || s === 'HUMAN_ASSIGNED' || s === 'ASSIGNED' || c.status === 2;
          case 'CLOSED':
            return s === 'CLOSED' || s === 'RESOLVED' || c.status === 3;
          default:
            return true;
        }
      });
    }

    // 3. Search keyword filter
    if (searchKeyword.trim()) {
      const q = searchKeyword.toLowerCase().trim();
      result = result.filter((c) => {
        const name = (c.customerName || '').toLowerCase();
        const assigned = (c.assignedUserName || '').toLowerCase();
        const email = (c.customerEmail || '').toLowerCase();
        const phone = (c.customerPhone || '').toLowerCase();
        const title = (c.title || '').toLowerCase();
        const lastMsgText =
          typeof c.lastMessage === 'object' && c.lastMessage !== null
            ? (c.lastMessage.content || '')
            : (c.lastMessage || '');
        const lastMsg = lastMsgText.toLowerCase();
        const senderName =
          typeof c.lastMessage === 'object' && c.lastMessage !== null
            ? (c.lastMessage.senderName || '').toLowerCase()
            : '';
        return (
          name.includes(q) ||
          assigned.includes(q) ||
          email.includes(q) ||
          phone.includes(q) ||
          title.includes(q) ||
          lastMsg.includes(q) ||
          senderName.includes(q)
        );
      });
    }

    // 4. Sort order
    result.sort((a, b) => {
      const aCreated = new Date(a.createdAtUtc || a.startedAt || 0).getTime();
      const bCreated = new Date(b.createdAtUtc || b.startedAt || 0).getTime();
      const aLastMsgTime =
        typeof a.lastMessage === 'object' && a.lastMessage !== null
          ? a.lastMessage.createdAtUtc
          : null;
      const bLastMsgTime =
        typeof b.lastMessage === 'object' && b.lastMessage !== null
          ? b.lastMessage.createdAtUtc
          : null;
      const aUpdated = new Date(aLastMsgTime || a.lastMessageAt || a.updatedAtUtc || a.createdAtUtc || a.startedAt || 0).getTime();
      const bUpdated = new Date(bLastMsgTime || b.lastMessageAt || b.updatedAtUtc || b.createdAtUtc || b.startedAt || 0).getTime();

      if (sortOrder === 'OLDEST_WAITING') {
        const aWaiting =
          String(a.status).toUpperCase() === 'WAITINGFORHUMAN' ||
          String(a.status).toUpperCase() === 'WAITING' ||
          a.status === 1;
        const bWaiting =
          String(b.status).toUpperCase() === 'WAITINGFORHUMAN' ||
          String(b.status).toUpperCase() === 'WAITING' ||
          b.status === 1;

        if (aWaiting && !bWaiting) return -1;
        if (!aWaiting && bWaiting) return 1;

        return aCreated - bCreated;
      }

      // Default: NEWEST_ACTIVITY
      return bUpdated - aUpdated;
    });

    return result;
  }, [rawConversations, selectedStoreId, statusFilter, searchKeyword, sortOrder]);

  return (
    <div
      id="tour-conversation-list"
      className={cn('flex flex-col h-full bg-card border-r border-border min-w-0 select-none', className)}
    >
      {/* Top Filter Controls */}
      <ConversationFilters />

      {/* Conversations Scrollable List Area */}
      <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-border/40">
        {isLoading && (
          <div className="p-3 space-y-3">
            {[1, 2, 3, 4, 5].map((idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-border/50">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="space-y-2 flex-1">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && isError && (
          <div className="p-6 text-center">
            <EmptyState
              icon={AlertCircle}
              title="Falha ao carregar as conversas"
              description="Ocorreu um erro ao buscar os registros de conversas."
              actionLabel="Tentar Novamente"
              onAction={() => refetch()}
            />
          </div>
        )}

        {!isLoading && !isError && filteredConversations.length === 0 && (
          <div className="p-6 flex-1 flex flex-col items-center justify-center">
            {rawConversations.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="Nenhuma conversa ainda"
                description="Quando os visitantes entrarem em contato com suas lojas, as conversas aparecerão aqui em tempo real."
              />
            ) : (
              <EmptyState
                icon={MessageSquare}
                title="Nenhuma conversa encontrada"
                description="Nenhum chat corresponde à sua loja, status ou filtros de busca ativos."
                actionLabel="Limpar Filtros"
                onAction={resetFilters}
              />
            )}
          </div>
        )}

        {!isLoading &&
          !isError &&
          filteredConversations.length > 0 && (
            <div className="pb-4">
              {filteredConversations.map((conversation) => (
                <ConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  showStoreBadge={!selectedStoreId || selectedStoreId === 'ALL'}
                />
              ))}
              {hasNextPage && (
                <div className="p-4 flex justify-center">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="px-4 py-2 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors disabled:opacity-50"
                  >
                    {isFetchingNextPage ? 'Carregando mais...' : 'Carregar mais conversas'}
                  </button>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default ConversationList;
